<template>
    <div class="filter__results">
        <!-- Title and filter status line - always shown except during initial load -->
        <div v-if="!isInitialLoad">
            <Title :title="pageTitle" />

            <div class="filterStatusLine">
                <TagCloud
                    class="mb-1 pb-2 pb-lg-0 mb-lg-0"
                    :badge-items="badgeItems"
                    :clear-all-label="propsData.clearAllLabel"
                    :remove-filter-label="propsData.removeFilterLabel"
                    @clear-filters="$emit('clear-filters')"
                    @remove-filter="(filterName, filterValue, optionId) => emit('remove-filter', filterName, filterValue, optionId)"
                />

<!--                <ModelSeriesChips :series="modelSeriesChips" />-->

                <Sorting
                    :sort="sort"
                    :sorting-options="propsData.sortingOptions"
                    :sort-by-label="propsData.sortByLabel"
                    :total-results-text="totalResultsFinalText"
                    @update:sort="value => emit('update:sort', value)"
                />
            </div>

            <!-- comparison bar -->
            <div v-if="comparisonList.length > 0" class="mt-3 mb-4">
                <CarComparisonBar
                    :comparison-list="comparisonList"
                    :header-label="propsData.comparisonHeaderLabel"
                    :compare-button-label="propsData.comparisonCompareButtonLabel"
                    :reset-button-label="propsData.comparisonResetButtonLabel"
                    :remove-label="propsData.comparisonRemoveLabel"
                    :max-compared-cars="10"
                    :compare-url="propsData.comparisonUrl"
                    @remove-from-comparison="emit('remove-from-comparison', $event)"
                    @clear-comparison="emit('clear-comparison')"
                />
            </div>
        </div>

        <!-- Full skeleton for initial load -->
        <div v-if="isInitialLoad" class="results-skeleton">
            <div class="skeleton-pulse"></div>
            <!-- Title skeleton -->
            <div class="skeleton-title mb-4"></div>

            <!-- Filter status line skeleton -->
            <div class="filterStatusLine mb-4">
                <div class="skeleton-tag-cloud mb-3">
                    <div class="skeleton-tag"></div>
                    <div class="skeleton-tag"></div>
                    <div class="skeleton-tag"></div>
                </div>

                <div class="d-flex justify-content-between align-items-center">
                    <div class="skeleton-text skeleton-text-sm"></div>
                </div>
            </div>

            <!-- Car cards skeleton -->
            <div class="row justify-content-start g-4 space-bottom">
                <div v-for="i in 6" :key="i" :class="localListDisplayType ? 'col-12' : 'col-sm-6 col-xl-4'">
                    <div class="skeleton-card" :class="{ 'skeleton-card-list': localListDisplayType }">
                        <div class="skeleton-card-image"></div>
                        <div class="skeleton-card-content">
                            <div class="skeleton-card-title"></div>
                            <div class="skeleton-card-subtitle"></div>
                            <div class="skeleton-card-details">
                                <div class="skeleton-card-detail"></div>
                                <div class="skeleton-card-detail"></div>
                                <div class="skeleton-card-detail"></div>
                            </div>
                            <div class="skeleton-card-price"></div>
                            <div class="skeleton-card-actions"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination skeleton -->
            <div class="d-flex justify-content-center mt-4">
                <div class="skeleton-pagination"></div>
            </div>
        </div>

        <!-- Car cards skeleton for subsequent loading -->
        <div v-else-if="loading && !isInitialLoad">
            <!-- Car cards skeleton -->
            <div class="row justify-content-start g-4 space-bottom">
                <div v-for="i in 18" :key="i" :class="localListDisplayType ? 'col-12' : 'col-sm-6 col-xl-4'">
                    <div class="skeleton-card" :class="{ 'skeleton-card-list': localListDisplayType }">
                        <div class="skeleton-card-image"></div>
                        <div class="skeleton-card-content">
                            <div class="skeleton-card-title"></div>
                            <div class="skeleton-card-subtitle"></div>
                            <div class="skeleton-card-details">
                                <div class="skeleton-card-detail"></div>
                                <div class="skeleton-card-detail"></div>
                                <div class="skeleton-card-detail"></div>
                            </div>
                            <div class="skeleton-card-price"></div>
                            <div class="skeleton-card-actions"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination skeleton -->
            <div class="d-flex justify-content-center mt-4">
                <div class="skeleton-pagination"></div>
            </div>
        </div>

        <!-- Actual car cards when not loading -->
        <div v-else>
            <div class="row justify-content-start g-4 space-bottom">
                <div
                    v-for="offer in offers"
                    :key="offer.id"
                    :class="localListDisplayType ? 'col-12' : 'col-sm-6 col-xl-4'"
                >
                    <CarCard
                        :offer="offer"
                        :isCompared="comparisonList.includes(offer.id)"
                        :isFavorite="favorites.includes(offer.id)"
                        :isDropdownOpen="openDropdownId === offer.id"
                        :lbl-from="propsData.lblFrom"
                        :lbl-monthly-incl-vat="propsData.lblMonthlyInclVat"
                        :lbl-monthly-excl-vat="propsData.lblMonthlyExclVat"
                        :lbl-excl-vat-simple="propsData.lblExclVatSimple"
                        :lbl-add-to-favorites="propsData.lblAddToFavorites"
                        :lbl-remove-from-favorites="propsData.lblRemoveFromFavorites"
                        :lbl-add-to-comparison="propsData.lblAddToComparison"
                        :lbl-remove-from-comparison="propsData.lblRemoveFromComparison"
                        :lbl-dropdown-add-to-favorites="propsData.lblDropdownAddToFavorites"
                        :lbl-dropdown-remove-from-favorites="propsData.lblDropdownRemoveFromFavorites"
                        :lbl-dropdown-add-to-comparison="propsData.lblDropdownAddToComparison"
                        :lbl-dropdown-remove-from-comparison="propsData.lblDropdownRemoveFromComparison"
                        :lbl-added-to-favorites="propsData.lblAddedToFavorites"
                        :lbl-removed-from-favorites="propsData.lblRemovedFromFavorites"
                        :lbl-added-to-comparison="propsData.lblAddedToComparison"
                        :lbl-removed-from-comparison="propsData.lblRemovedFromComparison"
                        :lbl-compared-cars-limit-reached="propsData.lblComparedCarsLimitReached"
                        :lbl-share="propsData.lblShare"
                        :lbl-details="propsData.lblDetails"
                        :lbl-show="propsData.lblShow"
                        :lbl-more-actions="propsData.lblMoreActions"
                        :volvo-selekt-tooltip="propsData.volvoSelektTooltip"
                        :badge-translations="propsData.badgeTranslations"
                        :currency-code="propsData.currencyCode"
                        :display-type="localListDisplayType ? 'list' : 'grid'"
                        :no-images-text="propsData.noImagesText"
                        @add-to-comparison="emit('add-to-comparison', $event)"
                        @remove-from-comparison="emit('remove-from-comparison', $event)"
                        @add-to-favorites="emit('add-to-favorites', $event)"
                        @remove-from-favorites="emit('remove-from-favorites', $event)"
                        @toggle-dropdown="emit('toggle-dropdown', $event)"
                        @car-selected="emit('car-selected', $event)"
                    />
                </div>
            </div>
        </div>

        <!-- Pagination section - always shown except during initial load -->
        <div v-if="!loading || !isInitialLoad" class="row align-items-center">
            <div class="col-12 d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-4">
                <!-- Left side spacer -->
                <div></div>

                <!-- Load More Button (center) - only when not loading -->
                <div v-if="!loading" class="mb-3 mb-md-0">
                    <button
                        v-if="currentPage < lastPage"
                        @click="emit('fetch-results', currentPage + 1, true)"
                        class="btn btn-primary"
                    >
                        {{ propsData.showMoreResults }}
                    </button>
                </div>

                <!-- Pagination (right side) - only when not loading -->
                <div v-if="!loading">
                    <Pagination
                        :current-page="currentPage"
                        :last-page="lastPage"
                        :on-each-side="5"
                        @page-click="handlePageClick"
                    />
                </div>

                <!-- Note: Pagination skeleton is already shown in the skeleton sections when loading -->
            </div>
        </div>

        <!-- SEO Content -->
        <div v-if="!loading && (seoHeadline || seoDescription)" class="space-padding-top-0-sm space-padding-top">
            <div class="bg-grey px-4 py-3 rounded-3">
                <div class="px-sm-3 pt-2 pt-sm-3 pb-sm-2">
                    <h2 v-if="seoHeadline" class="h4 mb-3">{{ seoHeadline }}</h2>

                    <div v-if="seoDescription" class="showMoreEllipsisContent" :class="{ 'ellipsis-4': isTextTruncated }">
                        <div v-html="seoDescription"></div>
                    </div>

                    <button
                        class="showMoreEllipsisBtn btn btn--chevron text-primary text--16px d-inline-flex align-items-center px-0 h-auto"
                        :class="{ 'd-none': !isTextTruncated || !showMoreButton }"
                        @click="showFullText"
                    >
                        {{ propsData.btnShowMoreSeoText || 'Show more' }}
                        <img class="icon icon--arrow-sm ms-2 transition" src="/images/icons/ico-dropdown-primary.svg" alt="">
                    </button>

                    <button
                        class="showLessEllipsisBtn btn btn--chevron text-primary d-inline-flex align-items-center px-0 mt-3"
                        :class="{ 'd-none': isTextTruncated }"
                        @click="truncateText"
                    >
                        {{ propsData.btnShowLessSeoText || 'Show less' }}
                        <img class="icon icon--arrow-sm ms-2 transition rotate-180" src="/images/icons/ico-dropdown-primary.svg" alt="">
                    </button>
                </div>
            </div>
        </div>

        <!-- Review Slider -->
        <ReviewSlider
            v-if="!loading && propsData.siteId && propsData.lang"
            :site-id="propsData.siteId"
            :lang="propsData.lang"
            :reviews-title="propsData.reviewsTitle"
        />
    </div>
</template>

<script setup>
import Title from '@/components/CarFilter/CarFilterTitle.vue'
import TagCloud from '@/components/CarFilter/CarFilterTagCloud.vue'
import ModelSeriesChips from '@/components/CarFilter/CarFilterModelSeriesChips.vue'
import Sorting from '@/components/CarFilter/CarFilterSorting.vue'
import CarCard from '@/components/CarFilter/CarCard.vue'
import Pagination from '@/components/CarFilter/Pagination.vue'
import CarComparisonBar from '@/components/CarFilter/CarComparisonBar.vue'
import ReviewSlider from '@/components/CarFilter/ReviewSlider.vue'
import { useCarFilterResults } from '@/composables/useCarFilterResults'

// Props definition
const props = defineProps({
    offers: Array,
    loading: Boolean,
    isInitialLoad: Boolean,
    pageTitle: String,
    totalResultsFinalText: String,
    badgeItems: Array,
    modelSeriesChips: Array,
    comparisonList: Array,
    favorites: Array,
    currentPage: Number,
    lastPage: Number,
    sort: String,
    listDisplayType: Boolean,
    propsData: Object,
    openDropdownId: [Number, null],
    seoHeadline: String,
    seoDescription: String,
})

// Emit events
const emit = defineEmits([
    'fetch-results',
    'add-to-comparison',
    'remove-from-comparison',
    'add-to-favorites',
    'remove-from-favorites',
    'toggle-dropdown',
    'remove-filter',
    'clear-filters',
    'update:sort',
    'update:list-display-type',
    'clear-comparison',
    'car-selected',
])

// Use the composable for shared logic
const {
    handlePageClick,
    localSort,
    localListDisplayType,
    tagCloud,
    canScrollPrev,
    canScrollNext,
    updateScrollButtons,
    onScroll,
    isTextTruncated,
    showMoreButton,
    seoContentRef,
    showFullText,
    truncateText,
    checkTextLength
} = useCarFilterResults(props, emit)
</script>

<style scoped>
</style>
